if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        // Main viewer navigation arrows (< and >). They are siblings of the media
        // list, not descendants of a modal-opener, so a single click listener is
        // enough — stopPropagation only guards against ancestor handlers.
        this.querySelector('[data-rg-viewer-prev]')?.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.navigateGallery(-1);
        });

        this.querySelector('[data-rg-viewer-next]')?.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.navigateGallery(1);
        });

        this.bindCircularSwipe();

        if (!this.elements.thumbnails) return;

        // Update the thumbnail the moment a swipe changes the slide (no debounce). While an arrow/thumbnail click is
        // still scrolling the viewer, the in-between slides are ignored (see pendingMediaId) so the highlight
        // doesn't flicker through them; a new touch/pointer press hands control back to the user.
        this.elements.viewer.addEventListener('slideChanged', this.onSlideChanged.bind(this));
        this.elements.viewer.addEventListener(
          'pointerdown',
          () => {
            this.pendingMediaId = null;
          },
          { passive: true }
        );
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            ?.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      navigateGallery(direction) {
        if (!this.elements.viewer) return;
        const slides = Array.from(this.elements.viewer.querySelectorAll('ul > [data-media-id]'));
        if (!slides.length) return;
        const activeIndex = slides.findIndex((slide) => slide.classList.contains('is-active'));
        const currentIndex = activeIndex !== -1 ? activeIndex : 0;
        const nextIndex = (currentIndex + direction + slides.length) % slides.length;
        const nextMediaId = slides[nextIndex].getAttribute('data-media-id');
        if (nextMediaId) {
          this.setActiveMedia(nextMediaId, false);
        }
      }

      // On mobile the slider is a native scroller, so it stops at the first/last slide. Swiping past either end
      // wraps around, matching the arrows: last -> first on a left swipe, first -> last on a right swipe.
      bindCircularSwipe() {
        const list = this.elements.viewer?.querySelector('.product__media-list');
        if (!list) return;
        let start = null;
        let atStart = false;
        let atEnd = false;

        list.addEventListener(
          'touchstart',
          (event) => {
            if (event.touches.length !== 1) {
              start = null;
              return;
            }
            start = { x: event.touches[0].clientX, y: event.touches[0].clientY };
            atStart = list.scrollLeft <= 1;
            atEnd = list.scrollLeft >= list.scrollWidth - list.clientWidth - 1;
          },
          { passive: true }
        );

        list.addEventListener(
          'touchend',
          (event) => {
            if (!start || this.mql.matches) return;
            const dx = event.changedTouches[0].clientX - start.x;
            const dy = event.changedTouches[0].clientY - start.y;
            start = null;
            if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
            if (atEnd && dx < 0) this.navigateGallery(1);
            else if (atStart && dx > 0) this.navigateGallery(-1);
          },
          { passive: true }
        );
      }

      onSlideChanged(event) {
        // Read the slide the viewer is on now (the event can be a stale, debounced intermediate position) and
        // keep `is-active` in step with it, so the arrows always continue from the slide being shown.
        const viewer = this.elements.viewer;
        const current = viewer.sliderItemsToShow?.[viewer.currentPage - 1] || event.detail.currentElement;
        if (!current) return;
        if (this.pendingMediaId) {
          if (current.dataset.mediaId === this.pendingMediaId) this.pendingMediaId = null;
          return;
        }
        viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.toggle('is-active', element === current);
        });
        const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${current.dataset.mediaId}"]`);
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        // Mobile: the viewer will now scroll to this slide; remember the target until it arrives.
        const shown = this.elements.viewer.sliderItemsToShow?.[this.elements.viewer.currentPage - 1];
        this.pendingMediaId = !this.mql.matches && shown && shown !== activeMedia ? activeMedia.dataset.mediaId : null;

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (!activeThumbnail) return;
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        // Thumbnails collapsed into the "+N View More" tile are display:none; there is nothing to scroll to.
        if (!thumbnail.offsetWidth) return;
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      // Only pauses: videos are never loaded/autoplayed on navigation (e.g. the "+N View More" thumbnail,
      // whose target is a video). They start when the customer clicks the poster's play button.
      playActiveMedia(activeItem) {
        window.pauseAllMedia();
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
